/**
 * Chargement asynchrone d'une ressource d'API, avec ses trois états explicites.
 *
 * Extrait de `pages/DetailElevePage.tsx` (SLX-078) pour être partagé par les
 * écrans de la console tuteur. Aucun changement de comportement : `loader` à
 * `null` = « pas encore prêt » (on reste en `loading`), annulation au démontage,
 * message d'erreur pris de `ApiError` quand il y en a un.
 *
 * `recharger()` rejoue le même `loader` — c'est ce qui permet à la file du matin
 * de se re-demander au serveur après une revue ou un report, plutôt que de
 * deviner localement ce que le serveur aurait recalculé.
 */
import { useCallback, useEffect, useState } from 'react';

import { ApiError } from '../api/client';

export type Resource<T> =
  | { readonly status: 'loading' }
  | { readonly status: 'ready'; readonly data: T }
  | { readonly status: 'error'; readonly message: string };

export function messageErreur(err: unknown): string {
  return err instanceof ApiError ? err.message : 'Erreur de chargement';
}

export function useAsync<T>(loader: (() => Promise<T>) | null): Resource<T> {
  return useAsyncRechargeable(loader).resource;
}

export interface AsyncRechargeable<T> {
  readonly resource: Resource<T>;
  /** Rejoue le loader courant (après une écriture qui change ce que le serveur renvoie). */
  readonly recharger: () => void;
}

export function useAsyncRechargeable<T>(loader: (() => Promise<T>) | null): AsyncRechargeable<T> {
  const [state, setState] = useState<Resource<T>>({ status: 'loading' });
  const [tick, setTick] = useState(0);

  useEffect(() => {
    if (!loader) return;
    let cancelled = false;
    setState({ status: 'loading' });
    loader()
      .then((data) => {
        if (!cancelled) setState({ status: 'ready', data });
      })
      .catch((err: unknown) => {
        if (!cancelled) setState({ status: 'error', message: messageErreur(err) });
      });
    return () => {
      cancelled = true;
    };
  }, [loader, tick]);

  const recharger = useCallback(() => setTick((t) => t + 1), []);
  return { resource: state, recharger };
}
